import { useEffect, useState } from 'react';
import webrtc from '@/services/webrtc';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Wifi, WifiOff } from 'lucide-react';

const WebRTCManager = ({ sipAccount, exten, onStatusChange }) => {
  const [status, setStatus] = useState({
    isConnected: false,
    isRegistered: false,
    error: null
  });

  useEffect(() => {
    if (exten) {
      initWebRTC();
    }
  }, [exten]);

  const initWebRTC = async () => {
    if (!exten) {
      toast.error('Extensão não configurada');
      return;
    }

    const config = {
      exten: exten,
      server: 'diskbot.com.br',
      displayName: exten
    };

    // Configurar callbacks
    webrtc.onRegistered = () => {
      setStatus({ isConnected: true, isRegistered: true, error: null });
      if (onStatusChange) onStatusChange({ isRegistered: true });
      toast.success('WebRTC: Conectado e registrado!');
    };

    webrtc.onUnregistered = () => {
      setStatus(prev => ({ ...prev, isRegistered: false }));
      if (onStatusChange) onStatusChange({ isRegistered: false });
      toast.warning('WebRTC: Desregistrado');
    };

    webrtc.onRegistrationFailed = (cause) => {
      setStatus({ isConnected: false, isRegistered: false, error: cause });
      if (onStatusChange) onStatusChange({ isRegistered: false, error: cause });
      toast.error(`WebRTC: Falha no registro - ${cause}`);
    };

    // Inicializar
    const result = await webrtc.init(config);
    if (!result.success) {
      toast.error(`Erro ao inicializar WebRTC: ${result.error}`);
      setStatus({ isConnected: false, isRegistered: false, error: result.error });
    }
  };

  const reconnect = () => {
    webrtc.disconnect();
    setTimeout(() => initWebRTC(), 500);
  };

  return (
    <Card className="border-2">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          {status.isRegistered ? (
            <><Wifi size={16} className="text-green-600" /> WebRTC Ativo</>
          ) : (
            <><WifiOff size={16} className="text-red-600" /> WebRTC Inativo</>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-600">Conectado:</span>
          {status.isConnected ? (
            <CheckCircle size={14} className="text-green-600" />
          ) : (
            <XCircle size={14} className="text-red-600" />
          )}
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-600">Registrado:</span>
          {status.isRegistered ? (
            <CheckCircle size={14} className="text-green-600" />
          ) : (
            <XCircle size={14} className="text-red-600" />
          )}
        </div>
        {status.error && (
          <p className="text-xs text-red-600 mt-2">Erro: {status.error}</p>
        )}
        {!status.isRegistered && (
          <Button
            onClick={reconnect}
            size="sm"
            variant="outline"
            className="w-full mt-2"
          >
            Reconectar
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default WebRTCManager;
